/**
 * Ski-set market guard (AAS-XXXX)
 *
 * Clears the cart when the shopper changes market while a ski set is in it.
 *
 * WHY
 * A ski set is ONE cart line — the ski variant, carrying the binding/skin as
 * line-item properties, which a Cart Transform expands into ski + binding +
 * skin. The accessory prices on that line are cents captured in the market the
 * shopper was in at add-to-cart, so ANY market change makes them wrong.
 *
 * Worse, if the ski is not sold in the market they switch to, Shopify drops the
 * line from /cart.js while still holding its inventory, and it does not come
 * back to /cart.js on returning. The line is then unreachable from the
 * storefront: not in the cart JSON, so no line key, so nothing to change or
 * remove. Re-adding the set fails ("... ist bereits ausverkauft") because
 * `_SetLinkId` is freshly random on every add, so the new line can never merge
 * with the invisible one. /cart/clear.js is the only lever that releases it.
 *
 * Verified as a Shopify-side behaviour, not ours: it reproduces with this
 * script blocked entirely, and with a plain line carrying any property that
 * differs on re-add. Plain products appear unaffected only because their
 * properties match on re-add and therefore merge. If Shopify fixes that, this
 * whole file can go.
 *
 * WHY STATE, NOT AN EVENT
 * There is no market-change event to listen for. The switcher submits a
 * localization form and the page reloads, but the market can equally change
 * from a URL, a locale-prefixed link, or a redirect — none of which go near
 * that button. So this compares the market it sees now against the market it
 * last saw, which catches every route in.
 *
 * SCOPE
 * Only fires when the cart HELD a set. Ordinary carts are never touched. It
 * does clear the WHOLE cart, so unrelated items go too — /cart/clear.js is the
 * only thing that reaches an invisible line, and a line we cannot see is a line
 * we cannot remove individually.
 */
(() => {
  const STATE_KEY = 'skiSetCartMarket';
  const SET_ID_KEY = '_SetLinkId';

  if (window.Shopify && window.Shopify.designMode) return;

  const localeRoot = () =>
    (window.theme?.routes?.root_url || window.Shopify?.routes?.root || '/').replace(/\/$/, '');

  // Market identity, not just currency: DE -> AT is a real market change that
  // never changes the currency, and would otherwise go undetected.
  const marketId = () => {
    const m = window.theme?.market || {};
    const parts = [m.handle || '', m.country_code || '', m.currency_code || window.Shopify?.currency?.active || ''];
    return parts.join('|') === '||' ? '' : parts.join('|');
  };

  const read = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
    } catch (e) {
      return null; // storage blocked — the guard simply never fires
    }
  };

  const write = (market, hadSet) => {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify({ market, hadSet }));
    } catch (e) {
      /* nothing to remember with */
    }
  };

  const refreshCartUI = () => {
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((el) => {
      try {
        el.onCartUpdate?.();
      } catch (e) {
        /* a stale cart section is not worth throwing over */
      }
    });
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ski-set-market-guard' });
      }
    } catch (e) {
      /* count badge catches up on the next navigation */
    }
  };

  // Shopify's `t` filter HTML-escapes every key that does not end in `_html`, so
  // an apostrophe reaches JS as an entity: "aren&#39;t". The toast writes with
  // textContent, which would print that entity literally. Decoding here rather
  // than renaming the key to `_html` keeps the string honestly marked as plain
  // text. Not an English-only concern — fr "n'est", it "dall'" hit it too.
  const decodeEntities = (str) => {
    if (!str || str.indexOf('&') === -1) return str;
    const helper = document.createElement('textarea');
    helper.innerHTML = str;
    return helper.value;
  };

  // Tell the shopper. Unlike a line that vanishes on its own, this is a removal
  // we performed, and they may well have been looking at the cart when it went.
  const notify = () => {
    const message = decodeEntities(window.skiSetStrings?.marketRemoved);
    if (!message) return;
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartError, { message });
      }
    } catch (e) {
      /* the toast is a courtesy — never let it break the clear */
    }
  };

  async function run() {
    const market = marketId();
    if (!market) return;

    const prior = read();

    if (prior && prior.market && prior.market !== market && prior.hadSet) {
      const res = await fetch(`${localeRoot()}/cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      }).catch(() => null);

      // Only record success. A failed clear leaves the flag set so the next
      // load tries again, rather than marking the cart clean while the
      // inventory-holding line is still there.
      if (!res || !res.ok) return;

      write(market, false);
      notify();
      refreshCartUI();
      return;
    }

    await recordCartState(market);
  }

  // Re-read the cart and remember whether it holds a set.
  async function recordCartState(market) {
    const id = market || marketId();
    if (!id) return;
    const cart = await fetch(`${localeRoot()}/cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    if (!cart || !Array.isArray(cart.items)) return;
    write(id, cart.items.some((i) => i.properties && i.properties[SET_ID_KEY]));
  }

  // Adding to cart is AJAX — there is NO page load between configuring a set and
  // switching market. Recording only on load would leave the flag reading
  // "hadSet: false" from before the add, and the guard would sit out the exact
  // flow it exists for: land on PDP -> add set -> switch market.
  //
  // So watch cart mutations directly rather than trusting a page load to happen.
  // The fetch wrapper (rather than the cartUpdate pub/sub) is deliberate: not
  // every path publishes that event — assets/cart-count-sync.js documents the
  // quick-add and wishlist paths calling renderContents() without it — but every
  // path posts to one of these endpoints, including app-owned ones.
  const CART_MUTATIONS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

  function watchCartMutations() {
    if (window.__skiSetGuardWatching) return;
    window.__skiSetGuardWatching = true;

    const nativeFetch = window.fetch;
    window.fetch = function (...args) {
      const promise = nativeFetch.apply(this, args);
      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        // recordCartState() reads /cart.js, which is not in this list, so the
        // wrapper cannot re-enter itself.
        if (CART_MUTATIONS.some((endpoint) => url.includes(endpoint))) {
          promise
            .then((res) => {
              if (res && res.ok) recordCartState();
            })
            .catch(() => {
              /* the request's own caller owns this failure */
            });
        }
      } catch (e) {
        /* never let bookkeeping break somebody else's cart call */
      }
      return promise;
    };
  }

  // Installed immediately, not on DOMContentLoaded: an add-to-cart can fire
  // before the document finishes parsing, and a missed add is a missed clear.
  watchCartMutations();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
